import {
  Editor,
  EditorContent,
  EditorContext,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import * as Menus from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Box, Button, chakra, HStack, Icon, VStack } from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaListUl,
  FaListOl,
  FaQuoteLeft,
  FaUndo,
  FaRedo,
  FaLink,
  FaUnlink,
} from "react-icons/fa";
import { BodyDoc } from "@common/interfaces/KYDCampaign";

const ChakraEditor = chakra(EditorContent);

// @ts-ignore
const { BubbleMenu, FloatingMenu } = Menus;

const extensions = [
  TextStyleKit,
  StarterKit,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
];

function MenuBar({ editor }: { editor: Editor }) {
  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive("bold") ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive("italic") ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive("strike") ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isCode: ctx.editor.isActive("code") ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive("paragraph") ?? false,
        isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive("bulletList") ?? false,
        isOrderedList: ctx.editor.isActive("orderedList") ?? false,
        isCodeBlock: ctx.editor.isActive("codeBlock") ?? false,
        isBlockquote: ctx.editor.isActive("blockquote") ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        textAlignLeft: ctx.editor.isActive({ textAlign: "left" }) ?? false,
        textAlignCenter: ctx.editor.isActive({ textAlign: "center" }) ?? false,
        textAlignRight: ctx.editor.isActive({ textAlign: "right" }) ?? false,
        textAlignJustify:
          ctx.editor.isActive({ textAlign: "justify" }) ?? false,
        isLink: ctx.editor.isActive("link") ?? false,
      };
    },
  });

  // Block formatting options for the select
  const blockFormatOptions = useMemo(
    () => [
      { value: "paragraph", label: "Paragraph" },
      { value: "heading1", label: "H1" },
      { value: "heading2", label: "H2" },
      { value: "heading3", label: "H3" },
    ],
    []
  );

  // Get current active block format
  const getCurrentBlockFormat = () => {
    if (editorState.isParagraph)
      return { value: "paragraph", label: "Paragraph" };
    if (editorState.isHeading1) return { value: "heading1", label: "H1" };
    if (editorState.isHeading2) return { value: "heading2", label: "H2" };
    if (editorState.isHeading3) return { value: "heading3", label: "H3" };
    if (editorState.isHeading4) return { value: "heading4", label: "H4" };
    if (editorState.isHeading5) return { value: "heading5", label: "H5" };
    if (editorState.isHeading6) return { value: "heading6", label: "H6" };
    return null;
  };

  // Handle block format change
  const handleBlockFormatChange = (selectedOption: any) => {
    if (!selectedOption) {
      // If nothing selected, default to paragraph
      editor.chain().focus().setParagraph().run();
      return;
    }

    switch (selectedOption.value) {
      case "paragraph":
        editor.chain().focus().setParagraph().run();
        break;
      case "heading1":
        editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case "heading2":
        editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case "heading3":
        editor.chain().focus().toggleHeading({ level: 3 }).run();
        break;
      case "heading4":
        editor.chain().focus().toggleHeading({ level: 4 }).run();
        break;
      case "heading5":
        editor.chain().focus().toggleHeading({ level: 5 }).run();
        break;
      case "heading6":
        editor.chain().focus().toggleHeading({ level: 6 }).run();
        break;
    }
  };

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // update link
    try {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } catch (e) {
      alert(e.message);
    }
  }, [editor]);

  return (
    <Box className="control-group" p={2}>
      <VStack spacing={1} align="stretch">
        {/* Text formatting buttons */}
        <HStack spacing={1} flexWrap="wrap" className="button-group">
          <Button
            size="sm"
            variant={editorState.isBold ? "solid" : "outline"}
            colorScheme={editorState.isBold ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleBold().run()}
            isDisabled={!editorState.canBold}
            fontWeight="bold"
          >
            B
          </Button>
          <Button
            size="sm"
            variant={editorState.isItalic ? "solid" : "outline"}
            colorScheme={editorState.isItalic ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isDisabled={!editorState.canItalic}
            fontStyle="italic"
          >
            I
          </Button>
          <Button
            size="sm"
            variant={editorState.isStrike ? "solid" : "outline"}
            colorScheme={editorState.isStrike ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isDisabled={!editorState.canStrike}
            textDecoration="line-through"
          >
            S
          </Button>

          {/* Link buttons */}
          <Button
            size="sm"
            variant={editorState.isLink ? "solid" : "outline"}
            colorScheme={editorState.isLink ? "blue" : "gray"}
            onClick={setLink}
          >
            <Icon as={FaLink} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => editor.chain().focus().unsetLink().run()}
            isDisabled={!editorState.isLink}
          >
            <Icon as={FaUnlink} />
          </Button>

          {/* Text alignment buttons */}
          <Button
            size="sm"
            variant={editorState.textAlignLeft ? "solid" : "outline"}
            colorScheme={editorState.textAlignLeft ? "blue" : "gray"}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <Icon as={FaAlignLeft} />
          </Button>
          <Button
            size="sm"
            variant={editorState.textAlignCenter ? "solid" : "outline"}
            colorScheme={editorState.textAlignCenter ? "blue" : "gray"}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <Icon as={FaAlignCenter} />
          </Button>
          <Button
            size="sm"
            variant={editorState.textAlignRight ? "solid" : "outline"}
            colorScheme={editorState.textAlignRight ? "blue" : "gray"}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <Icon as={FaAlignRight} />
          </Button>
          <Button
            size="sm"
            variant={editorState.textAlignJustify ? "solid" : "outline"}
            colorScheme={editorState.textAlignJustify ? "blue" : "gray"}
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          >
            <Icon as={FaAlignJustify} />
          </Button>
          <Button
            size="sm"
            variant={editorState.isBulletList ? "solid" : "outline"}
            colorScheme={editorState.isBulletList ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <Icon as={FaListUl} />
          </Button>
          <Button
            size="sm"
            variant={editorState.isOrderedList ? "solid" : "outline"}
            colorScheme={editorState.isOrderedList ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <Icon as={FaListOl} />
          </Button>
          <Button
            size="sm"
            variant={editorState.isBlockquote ? "solid" : "outline"}
            colorScheme={editorState.isBlockquote ? "blue" : "gray"}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Icon as={FaQuoteLeft} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => editor.chain().focus().undo().run()}
            isDisabled={!editorState.canUndo}
          >
            <Icon as={FaUndo} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={() => editor.chain().focus().redo().run()}
            isDisabled={!editorState.canRedo}
          >
            <Icon as={FaRedo} />
          </Button>
        </HStack>

        {/* Block formatting select */}
        <Box className="button-group">
          <Select
            options={blockFormatOptions}
            value={getCurrentBlockFormat()}
            onChange={handleBlockFormatChange}
            placeholder="Select block format..."
            chakraStyles={{
              container: () => ({
                position: "relative",
                width: "100%",
              }),
            }}
          />
        </Box>

        {/* History buttons */}
        <HStack spacing={2} flexWrap="wrap" className="button-group"></HStack>
      </VStack>
    </Box>
  );
}

const MarkdownEditor = forwardRef<
  Editor,
  {
    value: BodyDoc;
    onChange: (value: BodyDoc) => void;
    isDisabled?: boolean;
  }
>(({ value, onChange, isDisabled = false }, ref) => {
  const editor = useEditor({
    editable: !isDisabled,
    editorProps: {
      attributes: {
        class: "editor-container",
      },
    },
    extensions: [
      StarterKit,
      TextStyleKit,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        protocols: ["http", "https"],
        isAllowedUri: (url, ctx) => {
          try {
            // construct URL
            const parsedUrl = url.includes(":")
              ? new URL(url)
              : new URL(`${ctx.defaultProtocol}://${url}`);

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false;
            }

            // disallowed protocols
            const disallowedProtocols = ["ftp", "file", "mailto"];
            const protocol = parsedUrl.protocol.replace(":", "");

            if (disallowedProtocols.includes(protocol)) {
              return false;
            }

            // only allow protocols specified in ctx.protocols
            const allowedProtocols = ctx.protocols.map((p) =>
              typeof p === "string" ? p : p.scheme
            );

            if (!allowedProtocols.includes(protocol)) {
              return false;
            }

            // disallowed domains
            const disallowedDomains = [];
            const domain = parsedUrl.hostname;

            if (disallowedDomains.includes(domain)) {
              return false;
            }

            // all checks have passed
            return true;
          } catch {
            return false;
          }
        },
        shouldAutoLink: (url) => {
          try {
            // construct URL
            const parsedUrl = url.includes(":")
              ? new URL(url)
              : new URL(`https://${url}`);

            // only auto-link if the domain is not in the disallowed list
            const disallowedDomains = [];
            const domain = parsedUrl.hostname;

            return !disallowedDomains.includes(domain);
          } catch {
            return false;
          }
        },
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          style: {
            textDecoration: "underline",
            color: "blue",
          },
          className: "editor-link",
        },
      }),
    ],
    content: value,

    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
  });

  // Expose the editor instance through the ref
  useImperativeHandle(ref, () => editor, [editor]);

  // Memoize the provider value to avoid unnecessary re-renders
  const providerValue = useMemo(() => ({ editor }), [editor]);

  return (
    <EditorContext.Provider value={providerValue}>
      <MenuBar editor={editor} />
      <ChakraEditor
        cursor="text"
        minH="100px"
        p={2}
        editor={editor}
        autoFocus={true}
      />
    </EditorContext.Provider>
  );
});

MarkdownEditor.displayName = "MarkdownEditor";

export default MarkdownEditor;
